import { ScrollSection } from "@/components/main-page/ScrollSection";

const CERTIFICATIONS = [
  {
    name: "Cyber Essentials",
    description: "UK government-backed cyber security certification. Independently verified.",
    status: "Certified",
    statusColor: "text-emerald-500 bg-emerald-500/20",
    // Shield with checkmark
    icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
  },
  {
    name: "GDPR",
    description: "Data processing agreements, subject access requests, right to erasure, and lawful basis documentation built in.",
    status: "Aligned",
    statusColor: "text-blue-500 bg-blue-500/10",
    // User privacy / shield-person
    icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
  },
  {
    name: "ISO 27001",
    description: "Information security management controls across infrastructure, access management, and incident response.",
    status: "Aligned",
    statusColor: "text-blue-500 bg-blue-500/10",
    // Document with checkmark
    icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  },
  {
    name: "SOC 2 Type II",
    description: "Trust service criteria for security, availability, and confidentiality embedded in platform architecture.",
    status: "Aligned",
    statusColor: "text-blue-500 bg-blue-500/10",
    // Clipboard check
    icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
  },
];

const FEATURES = [
  {
    label: "AES-256 encryption",
    description: "Data encrypted at rest and in transit across all services.",
    icon: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z",
  },
  {
    label: "UK / EU data residency",
    description: "All data stored and processed within UK and EU jurisdictions.",
    icon: "M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  },
  {
    label: "Human oversight",
    description: "Every AI output quality-checked. Full audit trail on all actions.",
    icon: "M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z",
  },
];

const CERT_LOGOS = [
  { src: "/cert-cyber-essentials.png", className: "absolute right-1 -bottom-2 h-[120%] w-auto" },
  { src: "/cert-gdpr.png", className: "absolute -right-4 top-1/2 -translate-y-1/2 h-[120%] w-auto" },
  { src: "/cert-iso27001.png", className: "absolute -right-4 top-1/2 -translate-y-1/2 h-[120%] w-auto" },
  { src: "/cert-soc2.png", className: "absolute -right-4 top-1/2 -translate-y-1/2 h-[120%] w-auto" },
];

export function ComplianceSection() {
  return (
    <ScrollSection id="compliance" title="Compliance and security" subtitle="Architected around the security and compliance frameworks that regulated industries require.">
      {/* Certification grid */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        {CERTIFICATIONS.map((cert, i) => (
          <div key={cert.name} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6 md:p-8 relative overflow-hidden">
            <img
              src={CERT_LOGOS[i]!.src}
              alt=""
              className={`${CERT_LOGOS[i]!.className} object-contain pointer-events-none`}
              style={{ opacity: 0.07 }}
              loading="lazy"
            />
            <div className="flex items-center gap-3 mb-3 relative">
              <span className="text-sm font-medium">{cert.name}</span>
              <span
                className={`text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase px-2 py-0.5 rounded ${cert.statusColor}`}
              >
                {cert.status}
              </span>
            </div>
            <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed relative">{cert.description}</p>
          </div>
        ))}
      </div>

      {/* Feature row */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {FEATURES.map((feature) => (
          <div key={feature.label} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6 md:p-8">
            <svg
              className="w-5 h-5 text-[var(--color-fg-tertiary)] mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d={feature.icon} />
            </svg>
            <div className="text-sm font-medium mb-2">{feature.label}</div>
            <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed">{feature.description}</p>
          </div>
        ))}
      </div>

      {/* FCA blockquote */}
      <blockquote className="border-l-2 border-[var(--color-accent)] pl-6 py-1 max-w-3xl">
        <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed">
          We don't bolt compliance on after the fact. Every agent ships with governance, audit trails, and human oversight built into the architecture.
        </p>
      </blockquote>
    </ScrollSection>
  );
}
